import type { TemplateParamCatalogOutput, TemplateVersion } from 'used-vehicle-contracts';
import { Badge } from '../../../components/ui/Badge';
import { DefinitionList } from '../../../components/ui/DefinitionList';
import { Select } from '../../../components/ui/Select';
import { formatDay, formatMoney, formatText } from '../../../lib/format';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { formatParamValue, groupParams, PARAM_GROUP_ORDER } from '../templates-display';

export interface TemplateValuesTabProps {
  version: TemplateVersion;
  versions: readonly TemplateVersion[];
  catalog: TemplateParamCatalogOutput | undefined;
  onSelectVersion: (versionId: string) => void;
}

/**
 * The values a version publishes, grouped exactly as the engine's catalogue groups them (plan §5.10).
 *
 * Fractions are printed as `%` and amounts as JOD — the storage stays a fraction, only this view
 * converts (plan §11.6). The two Arabic damage tables keep their original wording with an English gloss
 * beneath, because the inspection sheet the appraiser holds is written in those words (plan §2.7).
 */
export const TemplateValuesTab = ({ version, versions, catalog, onSelectVersion }: TemplateValuesTabProps) => {
  const { t } = useI18nTranslation();

  const grouped = groupParams(catalog?.params ?? []);

  const damageRows = (table: readonly number[], options: TemplateParamCatalogOutput['damageGroup1'] | undefined) =>
    (options ?? []).map((option) => ({
      label: option.arabic,
      gloss: option.english,
      value: table[option.index],
    }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            mono
            tone={version.status === 'PUBLISHED' ? 'success' : version.status === 'DRAFT' ? 'info' : 'muted'}
          >
            {t('templates.versionBadge', { version: version.version })}
          </Badge>
          <span className="text-xs text-muted-foreground">{t(`templates.versionStatuses.${version.status}`)}</span>
        </div>
        <div className="w-56">
          <label
            htmlFor="template-version-select"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('templates.viewVersion')}
          </label>
          <Select
            id="template-version-select"
            value={version.id}
            onChange={(event) => onSelectVersion(event.target.value)}
            options={versions.map((entry) => ({
              value: entry.id,
              label: `v${entry.version} · ${t(`templates.versionStatuses.${entry.status}`)}`,
            }))}
          />
        </div>
      </div>

      <section className="rounded-lg border border-border bg-card p-5">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('templates.referenceSection')}</h2>
        <DefinitionList
          items={[
            {
              label: t('templates.referencePriceDefault'),
              value:
                version.referencePriceDefault === null ? (
                  <span className="text-muted-foreground">{t('common.notRecorded')}</span>
                ) : (
                  <span
                    dir="ltr"
                    className="inline-block tabular-nums"
                  >
                    {formatMoney(version.referencePriceDefault, { exact: true })}
                  </span>
                ),
            },
            { label: t('templates.referenceSource'), value: formatText(version.referenceSource) },
            {
              label: t('templates.referenceDate'),
              value: version.referenceDate === null ? t('common.notRecorded') : formatDay(version.referenceDate),
            },
            { label: t('templates.reason'), value: formatText(version.reason) },
          ]}
        />
      </section>

      {PARAM_GROUP_ORDER.map((group) => {
        const definitions = grouped.get(group) ?? [];

        if (definitions.length === 0) {
          return null;
        }

        return (
          <section
            key={group}
            className="rounded-lg border border-border bg-card p-5"
          >
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t(`paramGroups.${group}`)}</h2>
            <DefinitionList
              items={definitions.map((definition) => ({
                label: t(`params.${definition.key}`, definition.label),
                value: (
                  <span className="flex flex-wrap items-center gap-2">
                    <span
                      dir="ltr"
                      className="inline-block font-mono text-sm tabular-nums"
                    >
                      {formatParamValue(version.params[definition.key], definition.unit)}
                    </span>
                    {definition.provisional ? <Badge tone="warning">{t('templates.provisional')}</Badge> : null}
                    {version.overridableKeys.includes(definition.key) ? <Badge>{t('templates.overridable')}</Badge> : null}
                  </span>
                ),
              }))}
            />
          </section>
        );
      })}

      {(
        [
          { key: 'damageGroup1' as const, table: version.damageGroup1, options: catalog?.damageGroup1 },
          { key: 'damageGroup2' as const, table: version.damageGroup2, options: catalog?.damageGroup2 },
        ] satisfies readonly {
          key: 'damageGroup1' | 'damageGroup2';
          table: readonly number[];
          options: TemplateParamCatalogOutput['damageGroup1'] | undefined;
        }[]
      ).map(({ key, table, options }) => (
        <section
          key={key}
          className="rounded-lg border border-border bg-card p-5"
        >
          <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t(`templates.${key}`)}</h2>
          <p className="mb-3 text-xs text-muted-foreground">{t('templates.damageGroupHint')}</p>
          <DefinitionList
            items={damageRows(table, options).map((row) => ({
              label: row.label,
              value: (
                <span className="flex flex-col gap-0.5">
                  <span
                    dir="ltr"
                    className="inline-block font-mono text-sm tabular-nums"
                  >
                    {formatMoney(row.value)}
                  </span>
                  <span className="text-xs text-muted-foreground">{row.gloss}</span>
                </span>
              ),
            }))}
          />
        </section>
      ))}
    </div>
  );
};
